import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Rating,
  Stack,
  Typography,
} from "@mui/material";
import api from "../../api";

/**
 * How a group rated one class.
 *
 * Loaded on demand rather than per row, so opening the class list stays one request.
 */
const ClassFeedbackSummaryDialog = ({ open, classroom, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !classroom?.id) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        setData(null);
        const res = await api.get(`tutor/classrooms/${classroom.id}/feedback`);
        if (!cancelled) setData(res.data?.data ?? null);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || "Could not load feedback.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, classroom?.id]);

  const total = data?.count ?? 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
        Class feedback
        <Typography variant="body2" color="text.secondary">
          {classroom?.name}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && total === 0 && (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            No ratings yet for this class.
          </Typography>
        )}

        {!loading && !error && total > 0 && (
          <>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {data.average?.toFixed(1)}
              </Typography>
              <Box>
                <Rating value={data.average} precision={0.1} readOnly />
                <Typography variant="body2" color="text.secondary">
                  {total} {total === 1 ? "response" : "responses"}
                </Typography>
              </Box>
            </Stack>

            {[5, 4, 3, 2, 1].map((star) => {
              const count = data.histogram?.[star] ?? 0;
              return (
                <Stack key={star} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" sx={{ width: 16 }}>{star}</Typography>
                  <LinearProgress
                    variant="determinate"
                    value={total ? (count / total) * 100 : 0}
                    sx={{ flex: 1, height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" sx={{ width: 20, textAlign: "right" }}>{count}</Typography>
                </Stack>
              );
            })}

            {data.presets?.some((p) => p.count > 0) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  What they said
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                  {data.presets
                    .filter((p) => p.count > 0)
                    .map((p) => (
                      <Chip key={p.key} size="small" label={`${p.label} · ${p.count}`} />
                    ))}
                </Stack>
              </>
            )}

            <Divider sx={{ my: 2 }} />
            <Stack spacing={1.5}>
              {data.responses?.map((r) => (
                <Box key={r.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Rating value={r.rating} readOnly size="small" />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {r.student?.name}
                    </Typography>
                  </Stack>
                  {r.preset_labels?.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {r.preset_labels.join(" · ")}
                    </Typography>
                  )}
                  {r.comment && (
                    <Typography variant="body2" sx={{ mt: 0.25 }}>
                      “{r.comment}”
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClassFeedbackSummaryDialog;
